import { CollectionPreferencesProps } from "@amzn/awsui-components-react-v3";
import { z } from "zod";

const preferencesSchema = z.object({
  contentDisplay: z.array(
    z.object({
      id: z.string(),
      visible: z.boolean(),
    }),
  ),
});

export interface TablePreferencesConfig {
  storageKey: string;
  defaults: CollectionPreferencesProps.Preferences;
}

/**
 * Saves the preferences to local storage. If the preferences match the provided
 * defaults, the preferences are not saved and are instead removed from local
 * storage altogether.
 */
export function saveTablePreferences(
  config: TablePreferencesConfig,
  preferences: CollectionPreferencesProps.Preferences,
) {
  const isDefault = config.defaults.contentDisplay?.every(
    (col, i) =>
      preferences.contentDisplay?.[i]?.id === col.id &&
      preferences.contentDisplay?.[i]?.visible === col.visible,
  );
  if (isDefault) {
    localStorage.removeItem(config.storageKey);
    return;
  }
  localStorage.setItem(config.storageKey, JSON.stringify(preferences));
}

/**
 * Loads the stored preferences from local storage and merges them with the
 * provided defaults to ensure that existing preferences do not prevent new
 * columns from being displayed. If nothing is stored, the provided defaults are
 * returned as is. If the stored value fails schema validation or JSON parsing,
 * the corrupt entry is removed from local storage and the defaults are returned
 * as is along with `failed: true`.
 */
export function loadTablePreferences(config: TablePreferencesConfig): {
  preferences: CollectionPreferencesProps.Preferences;
  failed: boolean;
} {
  try {
    const stored = localStorage.getItem(config.storageKey);
    if (!stored) return { preferences: config.defaults, failed: false };
    const parsed = preferencesSchema.parse(JSON.parse(stored));
    const storedById = new Map(parsed.contentDisplay.map((c) => [c.id, c]));
    const contentDisplay = config.defaults.contentDisplay?.map(
      (col) => storedById.get(col.id) ?? col,
    );
    return { preferences: { contentDisplay }, failed: false };
  } catch {
    localStorage.removeItem(config.storageKey);
    return { preferences: config.defaults, failed: true };
  }
}
